/**
 * The two themes. This file, and only this file, decides which ramp step plays which role.
 *
 * Dark is not a filter over light. Contrast inverts, so the accents move to the light end of
 * their ramps and take dark text, and the status fills that were 600/700 become 400. A theme
 * derived by inverting lightness produces glowing saturated buttons that hurt to look at in a
 * dim shop, which is the case this app is actually for.
 */

import {
  absolute,
  copper,
  danger,
  ground,
  info,
  moss,
  neutral,
  success,
  surfaceDark,
  warning,
} from '../primitives/color.js';
import type { ThemeColors } from './roles.js';

export const lightColors: ThemeColors = {
  surface: {
    base: ground.light,
    // White, not neutral-50: on a cream page a white card lifts, and neutral-50 is close enough
    // to the ground that the card edge disappears without a border doing the work.
    raised: absolute.white,
    overlay: absolute.white,
    sunken: neutral[100],
    inverse: neutral[900],
    backdrop: moss[100],
  },

  text: {
    primary: neutral[900], // 13.29:1 on the page
    secondary: neutral[700], // 6.85:1
    muted: neutral[600], // 4.77:1
    disabled: neutral[400], // 2.49:1 — see the note on the role
    inverse: neutral[50],
  },

  border: {
    subtle: neutral[200],
    default: neutral[300],
    strong: neutral[500], // 3.39:1 — the only neutral clearing 3:1 on both themes
    focus: copper[600],
    // 1.83:1 against a white card — a definite edge without becoming a box. A card is not a
    // control, so 1.4.11's 3:1 does not apply; holding it there would put a hard outline round
    // every panel and turn a dashboard of twelve into a spreadsheet.
    card: neutral[300],
  },

  brand: {
    base: moss[600],
    hover: moss[700],
    active: moss[800],
    subtle: moss[100],
    subtleText: moss[800],
    on: absolute.white, // 5.61:1
  },

  action: {
    base: copper[600],
    hover: copper[700],
    active: copper[800],
    subtle: copper[100],
    subtleText: copper[800],
    on: absolute.white, // 4.96:1
  },

  success: {
    // 700, not 600: success-600 against white measures 3.92:1 and misses AA for the label.
    solid: success[700],
    solidHover: success[800],
    on: absolute.white, // 5.89:1
    subtle: success[100],
    subtleText: success[800],
    border: success[400],
  },

  warning: {
    // The one status that fills light and takes dark text. A gold dark enough to carry white
    // stops looking like a warning and starts looking like a disabled copper button.
    solid: warning[400],
    solidHover: warning[500],
    on: neutral[900], // 6.06:1
    subtle: warning[100],
    subtleText: warning[800],
    border: warning[400],
  },

  danger: {
    solid: danger[600],
    solidHover: danger[700],
    on: absolute.white, // 4.96:1
    subtle: danger[100],
    subtleText: danger[800],
    border: danger[400],
  },

  info: {
    solid: info[700],
    solidHover: info[800],
    on: absolute.white,
    subtle: info[100],
    subtleText: info[800],
    border: info[400],
  },

  focus: {
    ring: copper[600],
    halo: ground.light,
  },

  delta: {
    positive: success[800],
    // Amber, not terracotta. See the role's own note: terracotta is reserved for the attention
    // queue, and 800 is the step that carries this as *text* on cream rather than as a fill.
    negative: warning[800],
    neutral: neutral[600],
  },

  // 0.55, not 0.42: at 0.42 what was behind a dialog stayed fully readable, which is a wash, not
  // a dim. A dialog exists to be the only thing on screen for a moment.
  scrim: 'rgb(38 30 20 / 0.55)',
  skeleton: neutral[200],
};

export const darkColors: ThemeColors = {
  surface: {
    base: surfaceDark.base,
    raised: surfaceDark.raised,
    overlay: surfaceDark.overlay,
    sunken: surfaceDark.sunken,
    inverse: neutral[100],
    backdrop: surfaceDark.sunken,
  },

  text: {
    primary: neutral[100], // 14.05:1 on the page
    secondary: neutral[300],
    muted: neutral[500], // 4.60:1
    disabled: neutral[700],
    inverse: neutral[900],
  },

  border: {
    subtle: neutral[800],
    default: neutral[700],
    strong: neutral[500], // 4.60:1
    focus: copper[400],
    // Dark already separates a card by surface tone (raised sits 4 points above base), so this
    // only has to confirm the edge rather than create it — 700, not the 500 light needs.
    card: neutral[700],
  },

  brand: {
    base: moss[400],
    hover: moss[300],
    active: moss[200],
    subtle: moss[900],
    subtleText: moss[200],
    on: neutral[900], // 5.07:1
  },

  action: {
    base: copper[400],
    hover: copper[300],
    active: copper[200],
    subtle: copper[900],
    subtleText: copper[200],
    on: neutral[900], // 4.83:1
  },

  success: {
    solid: success[400],
    solidHover: success[300],
    on: neutral[900], // 6.31:1
    subtle: success[900],
    subtleText: success[300],
    border: success[600],
  },

  warning: {
    solid: warning[400],
    solidHover: warning[300],
    on: neutral[900], // 6.06:1
    subtle: warning[900],
    subtleText: warning[300],
    border: warning[600],
  },

  danger: {
    solid: danger[400],
    solidHover: danger[300],
    on: neutral[900], // 5.32:1
    subtle: danger[900],
    subtleText: danger[300],
    border: danger[600],
  },

  info: {
    solid: info[400],
    solidHover: info[300],
    on: neutral[900],
    subtle: info[900],
    subtleText: info[300],
    border: info[600],
  },

  focus: {
    ring: copper[400],
    halo: surfaceDark.base,
  },

  delta: {
    positive: success[300],
    negative: warning[300],
    // 400 rather than `text.muted`'s 500, for the same reason the chart axis is: a delta is read
    // on a card, and 500 only clears AA against the page behind it.
    neutral: neutral[400],
  },

  scrim: 'rgb(0 0 0 / 0.62)',
  skeleton: neutral[800],
};

export const themes = { light: lightColors, dark: darkColors } as const;
